import * as React from 'react';
import { cloneExcept } from 'core/utils';

export type ButtonStyle = 'default' | 'primary' | 'success' | 'info' | 'warning' | 'danger' | 'danger2' | 'link';

interface Props extends React.HTMLProps<HTMLButtonElement> {
	buttonStyle?: ButtonStyle;
	outline?: boolean;
}

const getAccessKeysCandidates = (child: React.ReactNode): string[] => {

	if (!child)
		return [];

	if (typeof child === 'string' && child.indexOf('&') >= 0)
		// Los '& ' los trato como si escapara '&&'. No permitimos un accessKey de tipo ' ' (espacio en blanco)
		return (child.match(/(&.)/g) || []).map(x => x === '& ' ? '&&' : x);

	const children = (child as any)?.props?.children;

	if (!React.isValidElement(child) || !children)
		return [];

	return getAccessKeysCandidates(children);
};


const getAccessKey = (props: Props): string | undefined => {

	if (props.accessKey)
		return props.accessKey;

	// Ignoro accesskeys &, sólo los tratamos para escapar el caracter '&'
	const candidates = getAccessKeysCandidates(props.children).filter(x => x !== '&&');
	if (candidates.length === 0)
		return undefined;

	const accessKey = candidates[0][1].toLowerCase();
	return accessKey;
};


const addAccessKeySpan = (children: React.ReactNode, accessKey: string | undefined): React.ReactNode[] => {

	if (!children)
		return [];

	return React.Children.map(children, child => {

		if (typeof child === 'string') {
			// Por lo que sea puede haber más candidatos a accessKeys. En todos ellos hago el reemplazo
			let newContent = child;
			getAccessKeysCandidates(child).forEach(x => newContent = newContent.split(x).join(x[1]));
			return newContent;
		}

		const props = (child as any)?.props;
		const children = props?.children;

		if (!React.isValidElement(child) || !children) {
			return child;
		}

		return React.cloneElement(child, props, addAccessKeySpan(children, accessKey));
	}) || [];
};

export default React.forwardRef<HTMLButtonElement, Props>(function Button(props, ref) {

	let className = props.buttonStyle ? 'btn btn-' + props.buttonStyle : 'btn btn-default';

	if (props.outline)
		className += ' btn-outline ';

	if (props.className)
		className += ' ' + props.className;

	if (props.autoFocus)
		className += ' ' + 'auto-focus';

	const accessKey = getAccessKey(props);
	const children = addAccessKeySpan(props.children, accessKey);
	return <button ref={ref} accessKey={accessKey} {...cloneExcept(props, ['buttonStyle', 'outline', 'key', 'accessKey', 'ref'])} className={className} type='button'>{children}</button>;
});
